import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ApiFetch } from '../api/client';
import type { DashboardCardWidth } from './dashboardGridItemClassName';
import { fetchStorageDashboardSummary } from './fetchStorageDashboardSummary';
import { DashboardHeaderActions } from './DashboardHeaderActions';
import { DashboardLayoutControls, type DashboardCardConfig } from './DashboardLayoutControls';
import { DashboardLoadingState } from './DashboardLoadingState';
import { DashboardSortableGrid } from './DashboardSortableGrid';
import { renderStorageDashboardCard } from './renderStorageDashboardCard';
import {
  DEFAULT_STORAGE_DASHBOARD_ORDER,
  STORAGE_DASHBOARD_CARDS,
  type StorageDashboardCardId,
  type StorageDashboardSummary
} from './storageDashboardTypes';
import { useDashboardLayout } from './useDashboardLayout';

export interface StorageDashboardSectionProps {
  enabled: boolean;
  username: string | null;
  storageKeyPrefix: string;
  apiFetch: ApiFetch;
  openHref: string;
  openLabel: string;
  accessTitle?: string;
  accessSubtitle?: string;
  showHeaderActions?: boolean;
  pageHeader?: ReactNode;
  customizing?: boolean;
  onCustomizingChange?: (value: boolean) => void;
  /** Extra cards merged into dashboard order / customize (e.g. Assistant calculator). */
  additionalCards?: DashboardCardConfig[];
  renderAdditionalCard?: (id: string) => ReactNode;
  getAllowedWidths?: (id: string) => readonly DashboardCardWidth[] | undefined;
}

function isStorageCardId(id: string): id is StorageDashboardCardId {
  return (
    id === 'storageAccess' || id === 'recentActivity' || id === 'latestFiles'
  );
}

export function StorageDashboardSection({
  enabled,
  username,
  storageKeyPrefix,
  apiFetch,
  openHref,
  openLabel,
  accessTitle,
  accessSubtitle,
  showHeaderActions = true,
  pageHeader,
  customizing: customizingProp,
  onCustomizingChange,
  additionalCards = [],
  renderAdditionalCard,
  getAllowedWidths
}: StorageDashboardSectionProps) {
  const [summary, setSummary] = useState<StorageDashboardSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [customizingInternal, setCustomizingInternal] = useState(false);
  const customizing = customizingProp ?? customizingInternal;
  const setCustomizing = onCustomizingChange ?? setCustomizingInternal;

  const allCards = useMemo<DashboardCardConfig[]>(
    () => [...STORAGE_DASHBOARD_CARDS, ...additionalCards],
    [additionalCards]
  );

  const defaultOrder = useMemo(
    () => [
      ...DEFAULT_STORAGE_DASHBOARD_ORDER,
      ...additionalCards.map((card) => card.id)
    ],
    [additionalCards]
  );

  const { layout, hidden, visibleOrder, moveCard, toggleCard, resetLayout, reorderCards, setCardWidth } =
    useDashboardLayout<string>(
      storageKeyPrefix,
      username,
      defaultOrder,
      undefined,
      getAllowedWidths
    );

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      setSummary(await fetchStorageDashboardSummary(apiFetch));
      setLastRefreshedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storage dashboard summary.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!enabled) return null;

  const headerActions = showHeaderActions ? (
    <DashboardHeaderActions
      loading={loading}
      customizing={customizing}
      lastRefreshedAt={lastRefreshedAt}
      onRefresh={() => void refresh()}
      onToggleCustomize={() => setCustomizing(!customizing)}
    />
  ) : null;

  return (
    <>
      {pageHeader || headerActions ? (
        <div className="dashboard-header">
          {pageHeader}
          {headerActions}
        </div>
      ) : null}

      {customizing ? (
        <DashboardLayoutControls
          cards={allCards}
          order={layout.order}
          hidden={hidden}
          onMove={moveCard}
          onToggle={toggleCard}
          onReset={resetLayout}
          onReorder={(fromIndex, toIndex) => reorderCards(fromIndex, toIndex)}
        />
      ) : null}

      {error ? (
        <div className="dashboard-error" role="alert">
          {error}
        </div>
      ) : null}

      {loading && !summary ? <DashboardLoadingState /> : null}

      {!loading && visibleOrder.length === 0 ? (
        <section className="dashboard-card">
          <p className="dashboard-empty">All dashboard cards are hidden. Use Customize to show at least one card.</p>
        </section>
      ) : null}

      {visibleOrder.length > 0 && (summary || visibleOrder.some((id) => !isStorageCardId(id))) ? (
        <DashboardSortableGrid
          customizing={customizing}
          visibleOrder={visibleOrder}
          widths={layout.widths}
          onWidthChange={setCardWidth}
          getAllowedWidths={getAllowedWidths}
          renderCard={(id) => {
            if (isStorageCardId(id)) {
              if (!summary) {
                return (
                  <section className="dashboard-card">
                    <p className="dashboard-empty">Loading storage card…</p>
                  </section>
                );
              }
              return renderStorageDashboardCard(id, summary, {
                openHref,
                openLabel,
                accessTitle,
                accessSubtitle
              });
            }
            return renderAdditionalCard?.(id) ?? null;
          }}
          onReorderVisible={(fromIndex, toIndex) =>
            reorderCards(fromIndex, toIndex, { visibleOnly: true })
          }
        />
      ) : null}
    </>
  );
}
